/// <reference types="react" />

import * as React from "react";
import * as Base from "DistributedTaskControls/Common/Components/Base";
import { ISearchableComboBoxInputProps, SearchableComboBoxInputComponent } from "DistributedTaskControls/SharedControls/InputControls/Components/SearchableComboBoxInputComponent";
import { InputControlType } from "DistributedTaskControls/SharedControls/InputControls/Common";
import {
    ComboBox,
    ComboBoxInputComponent,
    ComboBoxType,
    IComboBoxDropOptions,
    IProps
} from "DistributedTaskControls/SharedControls/InputControls/Components/ComboBoxInputComponent";

import * as Diag from "VSS/Diag";

import { IInputControlPropsBase } from "DistributedTaskControls/SharedControls/InputControls/Common";
import { TaskInputDefinitionBase as TaskInputDefinition } from "TFS/DistributedTaskCommon/Contracts";

import { Component } from "DistributedTaskControls/Common/Components/Base";
import * as Q from "q";
import { PickListInputUtility, IPickListRefreshOptions } from "DistributedTaskControls/SharedControls/InputControls/Components/PickListInputUtility";
import * as Utils_String from "VSS/Utils/String";
import { DtcUtils } from "DistributedTaskControls/Common/Utilities";

export interface ITaskSearchableComboBoxInputProps extends ISearchableComboBoxInputProps {
    options: IDictionaryStringTo<string>;
    onDropdownValueChanged?: (newValue: string) => void;
    onOptionsChanged?: (options: IDictionaryStringTo<string>) => void;
    getRefreshOptions?: () => IPickListRefreshOptions;
    inputDefinition: TaskInputDefinition;
    inputControlProps: IInputControlPropsBase<string>;
}

interface ITaskSearchableComboBoxInputStateBase extends Base.IState {
    inputItems: IDictionaryStringTo<string>;
    searchText: string;
    inputText: string;
}

export class SearchableComboBoxSearchState {
    isMoreDataAvailable: boolean;
}

export class TaskSearchableComboBoxInputComponent extends Base.Component<ITaskSearchableComboBoxInputProps, ITaskSearchableComboBoxInputStateBase> {

    constructor(props: ITaskSearchableComboBoxInputProps) {
        super(props);
        this.state = {
            inputItems: props.options || {},
            searchText: props.value || Utils_String.empty,
            inputText: props.value || Utils_String.empty
        };
    }

    public componentWillReceiveProps(newProps: ITaskSearchableComboBoxInputProps) {
        if (this._mounted) {
            this.setState({
                inputItems: newProps.options || this.state.inputItems ? this.state.inputItems : {},
                searchText: newProps.value || Utils_String.empty,
                inputText: newProps.value || Utils_String.empty,
            });
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._mounted = true;
    }

    public getType(): string {
        return InputControlType.INPUT_TYPE_PICK_LIST;
    }

    public render(): JSX.Element {
        Diag.logVerbose("[TaskSearchableComboBoxInputComponent.getControl]: Method called.");
        return (
            <SearchableComboBoxInputComponent
                source={this.getDisplayValueFromKeyValuePairs(this.state.inputItems) || []}
                onSearch={this._onCustomSearch}
                onRefresh={this._onRefresh}
                onDropdownOpened={this._onCustomDropdownOpened}
                onCustomValueChanged={this._onCustomValueChanged}
                getSearchButtonState={this._getSearchButtonState}
                updateSelectedValue={this._getSelectedValueDisplayValue}
                value={this.state.inputText}
                {...this.props} />
        );
    }

    private _onRefresh = (isManualRefresh: boolean): void => {
        if (!!this.props.getRefreshOptions && (!this.state.inputItems || Object.keys(this.state.inputItems).length === 0)) {
            PickListInputUtility.searchableComboboxOnRefresh(this.props.inputDefinition, this.props.getRefreshOptions(), this._searchState, isManualRefresh)
                .then((result) => {
                    this.setState({
                        inputItems: result
                    });

                    if (!!this.props.onOptionsChanged) {
                        this.props.onOptionsChanged(result);
                    }
                });
        }
    }

    private getDisplayValueFromKeyValuePairs(dict: { [key: string]: string }): string[] {
        let values: string[] = [];
        if (dict && Object.keys(dict).length > 0) {
            for (let key in dict) {
                if (dict.hasOwnProperty(key)) {
                    values.push(dict[key].toString());
                }
            }
        }

        return values;
    }

    private _getSelectedValueDisplayValue = (): string => {
        let result = Utils_String.empty;
        if (!!this.state && !!this.state.inputItems) {
            let values = this.state.inputItems;
            if (!!values) {
                for (let key in values) {
                    if (values.hasOwnProperty(key) && key === this.state.inputText) {
                        result = values[key].toString();
                    }
                }
            }

            if (!result) {
                result = this.state.inputText;
            }
        }

        return result;
    }

    private _onCustomSearch = (searchText: string): IPromise<any[]> => {
        let output: string[] = [];
        let q = Q.defer<string[]>();

        if (!!this.props.getRefreshOptions) {
            PickListInputUtility.onSearch(this.props.inputDefinition, this.props.getRefreshOptions(), searchText).then((result) => {
                this._appendItems(result);

                if (!!result) {
                    for (let key in result) {
                        output.push(result[key].toString());
                    }
                }

                output.sort((inputOptionA: string, inputOptionB: string) => {
                    return Utils_String.localeIgnoreCaseComparer(inputOptionA, inputOptionB);
                });
                q.resolve(output);
            }, (error) => {
                q.reject(error);
            });
        }

        return q.promise;
    }

    private _onCustomDropdownOpened = (): IPromise<any[]> => {
        let output: string[] = [];
        let q = Q.defer<string[]>();
        if (!!this.props.getRefreshOptions && (!this.state.inputItems || Object.keys(this.state.inputItems).length === 0)) {
            this.setState({ inputItems: {} }, () => {
                let refreshOptions = this.props.getRefreshOptions();

                PickListInputUtility.searchableComboboxOnRefresh(this.props.inputDefinition, refreshOptions, this._searchState, true)
                    .then((result) => {
                        this.setState({
                            inputItems: result
                        });
                        if (!!this.props.onOptionsChanged) {
                            this.props.onOptionsChanged(result);
                        }
                        if (!!result) {
                            for (let key in result) {
                                output.push(result[key].toString());
                            }
                        }

                        output.sort((inputOptionA: string, inputOptionB: string) => {
                            return Utils_String.localeIgnoreCaseComparer(inputOptionA, inputOptionB);
                        });
                        q.resolve(output);
                    }, (error) => {
                        q.reject(error);
                    });
            });
        }
        else {
            for (let key in this.state.inputItems) {
                output.push(this.state.inputItems[key].toString());
            }

            q.resolve(output);
        }

        return q.promise;
    }

    private _onCustomValueChanged = (newValue: string) => {
        let result = Utils_String.empty;

        if (!!this.state.inputItems) {
            for (let key in this.state.inputItems) {
                if (this.state.inputItems[key] === newValue) {
                    result = key;
                }
            }
        }

        if (!result) {
            this.setState({
                inputText: newValue
            });
        }

        if (!!result && !!this.props.onDropdownValueChanged) {
            this.props.onDropdownValueChanged(result);
        }
    }

    private _getSearchButtonState = (): SearchableComboBoxSearchState => {
        if (DtcUtils.getSearchDataSourceName(this.props.inputDefinition) || DtcUtils.isTaskInputSearchable(this.props.inputDefinition)) {
            this._searchState.isMoreDataAvailable = true;
        }

        return this._searchState;
    }

    private _appendItems(newItems: IDictionaryStringTo<string>): void {

        if (!newItems) {
            return;
        }

        let updatedItems: IDictionaryStringTo<string> = this.state.inputItems;

        for (let key in newItems) {
            if (!updatedItems.hasOwnProperty(key)) {
                updatedItems[key] = newItems[key];
            }
        }

        this.setState({
            inputItems: updatedItems
        });
    }

    private clone(obj) {
        let copy;
        if (null == obj || "object" !== typeof obj) {
            return obj;
        }

        if (obj instanceof Object) {
            copy = {};
            for (let attr in obj) {
                if (obj.hasOwnProperty(attr)) {
                    copy[attr] = this.clone(obj[attr]);
                }
            }
            return copy;
        }

        throw new Error("Unable to copy obj! Its type isn't supported.");
    }

    private _mounted: boolean;
    private _searchState: SearchableComboBoxSearchState = { isMoreDataAvailable: false };
}